import { QueryKeys } from "@/constants";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import httpClient from "@/services";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

// mfaSetupReq
export type IMfaSendOtpPayload = {
  divisionId: string;
  groupId: string;
  loginGroupId: string;
  username: string;
  accessToken?: string;
  email?: string | null; ///// test for add email
};

export type IMfaSendOtpResponse = {
  expiryTimestamp: string;
  message: string;
  signature: string;
  success: boolean;
  status: string;
  createdTimestamp: string;
};

// mfaSetup
export type IMfaVerifyOtpPayload = {
  divisionId: string;
  enteredOTP: string;
  groupId: string;
  loginGroupId: string;
  username: string;
  accessToken?: string;
  signature?: string;
};

export type IMfaVerifyOtpResponse = {
  message: string;
  success: boolean;
  status: string;
};

// disableMfa
export type IMfaDisablePayload = {
  username: string;
  password: string;
  accessToken?: string;
};

export type IMfaDisableResponse = {
  message: string;
  isMfaDisabled: boolean;
};

const mfaSetupOtp = async (info: IMfaSendOtpPayload) => {
  const payloadData = {
    divisionId: info.divisionId,
    groupId: info.groupId,
    loginGroupId: info.loginGroupId,
    username: info.username,
    email: info.email,
  };

  const config = {
    method: "POST",
    url: "otp/mfaSetupReq",
    headers: {
      Authorization: `Bearer ${info.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "1.0",
    },
    data: payloadData,
  };

  return httpClient.request(config).then((response) => response.data);
};

export const useMMfaSetupOtp = () => {
  const { addError } = useNotificationError();

  return useMutation({
    mutationKey: [QueryKeys.MFA_SETUP_OTP],

    mutationFn: (mfaSetupOtpPayload: IMfaSendOtpPayload) => {
      return mfaSetupOtp(mfaSetupOtpPayload);
    },

    onSuccess: (response: IMfaSendOtpResponse) => {
      if (!response.success) {
        addError({
          message: response.message,
          type: ErrorNotificationType.ERROR,
        });
      } else {
        addError({
          message: response.message,
          type: ErrorNotificationType.INFO,
        });
      }
    },

    onError: (error: AxiosError) => {
      addError({
        message: error.message,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

const mfaSetupStatus = async (info: IMfaVerifyOtpPayload) => {
  const payloadData = {
    divisionId: info.divisionId,
    enteredOTP: info.enteredOTP,
    groupId: info.groupId,
    loginGroupId: info.loginGroupId,
    username: info.username,
  };

  const config = {
    method: "POST",
    url: "otp/mfaSetup",
    headers: {
      Authorization: `Bearer ${info.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "1.0",
      signature: `${info.signature}`,
    },
    data: payloadData,
  };

  return httpClient.request(config).then((response) => response.data);
};

export const useMMfaSetupStatus = () => {
  const { addError } = useNotificationError();
  return useMutation({
    mutationKey: [QueryKeys.MFA_SETUP_STATUS],

    mutationFn: (mfaSetupStatusPayload: IMfaVerifyOtpPayload) => {
      return mfaSetupStatus(mfaSetupStatusPayload);
    },

    onSuccess: (response: IMfaVerifyOtpResponse) => {
      if (!response.success) {
        addError({
          message: response.message,
          type: ErrorNotificationType.ERROR,
        });
      }
      // else {
      //   addError({
      //     message: response.message,
      //     type: ErrorNotificationType.INFO,
      //   });
      // }
    },

    onError: (error: AxiosError) => {
      addError({
        message: error.message,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

const mfaDisable = async (info: IMfaDisablePayload) => {
  const payloadData = {
    username: info.username,
    password: info.password,
  };

  const config = {
    method: "POST",
    url: "otp/disableMfa",
    headers: {
      Authorization: `Bearer ${info.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "1.0",
    },
    data: payloadData,
  };

  return httpClient.request(config).then((response) => response.data);
};

export const useMMfaDisable = () => {
  return useMutation({
    mutationKey: [QueryKeys.MFA_DISABLE],
    mutationFn: (mfaDisablePayload: IMfaDisablePayload) => {
      return mfaDisable(mfaDisablePayload);
    },
  });
};
